<script lang="ts">
    import { createEventDispatcher } from 'svelte';
    import { get } from 'svelte/store';
    import { _ } from 'svelte-i18n';
    import { onMount } from 'svelte';
    import * as Sentry from '@sentry/browser';

    import TemplateExpandedSectionElement from './TemplateExpandedSectionElement.svelte';
    import TemplateExpandedSectionButtons from './TemplateExpandedSectionButtons.svelte';
    import TemplateExpandedSection from './TemplateExpandedSection.svelte';
    import { MapPointLocation } from '../../utilities/MapPointLocation';
    import MessageDanger from '../../shared/Messages/MessageDanger.svelte';
    import IconLocationOn from '../../shared/Icons/IconLocationOn.svelte';
    import ButtonSmallBlack from '../../shared/ButtonSmallBlack.svelte';
    import { onMapReady } from '../../utilities/onMapReady';
    import IconError from '../../shared/Icons/IconError.svelte';
    import Spinner from '../../shared/Spinner/Spinner.svelte';
    import type { JobResponseType } from './types';
    import { api, clientSettings } from '../../../stores/api';
    import { JobApi } from './JobApi';
    import InputText from '../../shared/InputText.svelte';
    import FormRow from './FormRow.svelte';
    import Label from './Label.svelte';
    import FormSection from './FormSection.svelte';
    import ErrorInline from '../../shared/ErrorInline.svelte';
    import SuccessInline from '../../shared/SuccessInline.svelte';
    import SelectDay from '../../shared/SelectDay.svelte';
    import SelectHour from '../../shared/SelectHour.svelte';
    import Checkbox from '../../shared/Checkbox/Checkbox.svelte';
    import { debug, jobs } from '../../../stores/store';
    import { getStatus } from '../../utilities/getStatus';
    import ErrorColor from '../../shared/ErrorColor.svelte';
    import JobStatusSegment from './JobStatusSegment.svelte';
    import { JobsOnMap } from '../Jobs/JobsOnMap.js';
    import JobOneAttributes from './JobOneAttributes.svelte';
    import {app} from '../../../stores/app';

    import './JobOne.css';
    import InfoColor from '../../shared/InfoColor.svelte';
    import JobTypeIcon from './JobTypeIcon.svelte';
    import { FriendlyDateGenerator } from '../../utilities/FriendlyDateGenerator';
    import ListElementFriendlyDate from '../../shared/ListElementFriendlyDate.svelte';
    import LinkSmallBlack from '../../shared/LinkSmallBlack.svelte';
    import { captureMessage } from '../../utilities/Sentry';
    import IconDoNotDisturbOn from '../../shared/Icons/IconDoNotDisturbOn.svelte';
    import WarningColor from '../../shared/WarningColor.svelte';
    import Switch from '../../shared/Switch.svelte';
    import JobHistory from './JobHistory/JobHistory.svelte';
    import { features } from '../../shared/Features/features';
    import { NotificationSystem } from '../NotificationSystem/NotificationSystem';
    import SelectVehicle from '../Vehicles/SelectVehicle.svelte';
    import type { VehicleResponseType } from '../Vehicles/types';
    import { VehiclesApi } from '../Vehicles/VehiclesApi';
    import IconFlashOn from '../../shared/Icons/IconFlashOn.svelte';
    import ModalInfo from '../../shared/Modals/ModalInfo.svelte';
    import { openModal } from 'svelte-modals';
    import { Job } from './Job';
    import { getJobColor } from './getJobColor';
    import JobServiceAttributes from './JobServiceAttributes.svelte';
    import IconWarning from '../../shared/Icons/IconWarning.svelte';
    import Opacity from '../../shared/Opacity.svelte';
    import DisplayAddress from './DisplayAddress.svelte';
    import { Tooltip, tooltip } from '@svelte-plugins/tooltips';
    import type { ErrorResponseType } from '@automapa/svelte-automapa-template/dist/Api';
    import { Navigation } from '../../utilities/Navigation';
    import JobModifyTimeWindowRuleMiniWidget from '../JobModifyTimeWindowRule/JobModifyTimeWindowRuleMiniWidget.svelte';

    export let job: JobResponseType;
    export let showHeader: boolean = false;
    export let switchable: boolean = true;
    export let closeButton: boolean = false;
    export let simpleView: boolean = false;
    export let index: number;
    export let expandedIndex: number = -1;
    export let jobsOnMap: JobsOnMap | null = null;
    export let fullEditableForm: boolean = false;
    export let enableMap: boolean = true;
    export let enableCheckbox: boolean = true;

    let error = '';
    let dispatch = createEventDispatcher();
    let geocodingInProgress: boolean | null = null;
    let target_date: string = ''; //(new Date().toISOString().substring(0,10));
    let checked: boolean = false;
    let friendlyTargetDate: FriendlyDateGenerator;
    let vehicles: VehicleResponseType[] = [];
    let jobClass: Job = new Job();

    function toggle() {
        if (switchable) {
            // https://er.automapa.pl/issues/41683
            expandedIndex = expandedIndex === index ? -1 : index;
            if (expandedIndex !== index) {
                console.log('toggle, closing job ', expandedIndex, index);
                jobsOnMap && jobsOnMap.jobShowBalloon(job.id, false);
                closePointOnMap();
                dispatch('close');
            } else {
                console.log('toggle, opening job ', expandedIndex, index);
                if (job.geom && job.geom.coordinates) {
                    jobsOnMap && jobsOnMap.zoomTo(job.geom.coordinates[0], job.geom.coordinates[1]);
                    jobsOnMap && jobsOnMap.jobShowBalloon(job.id);
                }
                dispatch('open');
            }
        }
    }

    async function onUpdate() {
        closePointOnMap();
        console.log('jobRequest, before parse', job);
        if (job.process_stage >= 20) {
            console.warn('Job cannot be changed', job.id, job.process_stage);
            error = 'job_cannot_be_changed_due_to_process_stage';
            return;
        }
        let checkJob: JobResponseType = await JobApi.getOne(get(api), job.id).catch(
            (err: ErrorResponseType) => {
                error = err.translation_id;
            }
        );
        if (checkJob && checkJob.process_stage >= 20) {
            console.warn('Job cannot be changed', job.id, job.process_stage);
            error = 'job_cannot_be_changed_due_to_process_stage';
            return;
        }

        let {
            address_id,
            address_attributes,
            client_attributes,
            special_attributes,
            target_date,
            geom,
            import_id,
            collection_id,
            webuser_id,
            job_type_id,
            rejected,
            coords_accepted,
            vehicle_id,
            coords_set_manually,
            ..._rest
        } = job;
        // let jobRequest = extractFieldsFromB<JobRequestType>(job, JobRequestSchema);
        // JobRequestSchema.parse(jobRequest);
        // console.log('jobRequest', jobRequest);
        // {address_id: null, client_attributes: null, job_type_id: 0, address_attributes, special_attributes, target_date, geom, import_id, collection_id, webuser_id }
        let response = await JobApi.updateOne(get(api), job.id, {
            address_id,
            client_attributes,
            job_type_id,
            address_attributes,
            special_attributes,
            target_date,
            geom,
            import_id,
            collection_id,
            webuser_id,
            rejected,
            coords_accepted,
            vehicle_id,
            coords_set_manually,
        }).catch((err: ErrorResponseType) => {
            error = err.translation_id;
        });
        if (response) {
            $NotificationSystem.reload();
            job = { ...response };
            console.log('JobOneRow, job', job);
            reloadVehicles();
            error = '';
            // trigger on:change
            dispatch('change', { job });
        }
    }

    let mapPointLocation: MapPointLocation | null;

    let startPointOnMap = function () {
        if (mapPointLocation) {
            if (!mapPointLocation.isCurrent()) {
                if (
                    mapPointLocation.start({
                        message: $_('Click on the map to set job #{count} coordinates', {
                            values: { count: job.id },
                        }),
                    })
                ) {
                    dispatch('pointonmapstart');
                }
            } else {
                mapPointLocation.close(true);
            }
        }
    };

    export const closePointOnMap = function () {
        if (mapPointLocation) {
            if (mapPointLocation.close(true)) {
                dispatch('pointonmapclose');
            }
        }
    };

    const setJobCompleted = function () {
        if (
            confirm(
                $_('Are you sure you want to set job #{count} as completed?', {
                    values: { count: job.id },
                })
            )
        ) {
            JobApi.setCompleted(get(api), job.id).then(response => {
                job = { ...response };
            });
        }
    };

    function reloadVehicles() {
        if (job.vehicle_id) {
            VehiclesApi.getOne(get(api), job.vehicle_id).then(v => {
                vehicles = [...vehicles, v];
            });
        }
    }

    function editable(job: JobResponseType) {
        return new Job().set(job).editable();
    }

    onMount(() => {
        Promise.all([onMapReady()]).then(response => {
            mapPointLocation = new MapPointLocation();
            mapPointLocation.addEventListener('setCoordinates', function (evt: any) {
                job.geom = {
                    type: 'Point',
                    coordinates: [evt.detail.coords.x, evt.detail.coords.y],
                };
                job.coords_accepted = true;
                job.coords_set_manually = true;
                onUpdate().then(() => {
                    dispatch('setcoordinates');
                });
            });
            mapPointLocation.addEventListener('close', function () {
                dispatch('pointonmapclose');
            });

            reloadVehicles();
        });

        return () => {
            if (mapPointLocation) {
                mapPointLocation.close(true);
            } else {
                captureMessage('captureMessage: mapPointLocation is not defined');
            }
        };
    });

    friendlyTargetDate = new FriendlyDateGenerator(
        job.target_date || '',
        $clientSettings.display.showRelativeToday
    );

    $: {
        if (job.target_date !== null) {
            target_date = job.target_date;
            friendlyTargetDate.set(job.target_date);
            friendlyTargetDate = friendlyTargetDate;
        }
    }

    $: checked =
        (($jobs.selectMode === 'filtered' && !$jobs.selectedOrUnselected.includes(job.id)) ||
            ($jobs.selectMode === 'selected' && $jobs.selectedOrUnselected.includes(job.id))) &&
        enableCheckbox;

    // $: {
    //     expandedIndex = expandedIndex;
    //     console.log('JobOneRow, expandedIndex', expandedIndex, index);
    // }

    $: {
        jobClass.set(job);
        jobClass = jobClass;
        console.log('jobClass, label', jobClass.getLabel());
    }
</script>

<div class="order-element order-element-data" class:simple-view={simpleView} class:checked>
    {#if !fullEditableForm}
        <div class="checkbox">
            {#if enableCheckbox}
                <Checkbox
                    disabled={false}
                    label=""
                    {checked}
                    onChange={() => {
                        if ($jobs.selectedOrUnselected.includes(job.id)) {
                            $jobs.selectedOrUnselected = $jobs.selectedOrUnselected.filter(
                                id => id !== job.id
                            );
                        } else {
                            $jobs.selectedOrUnselected = [...$jobs.selectedOrUnselected, job.id];
                        }
                    }}
                />
            {/if}
        </div>

        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <div
            class="address"
            class:switchable
            class:extended-background={expandedIndex === index}
            on:click={toggle}
            style="flex-direction: row;justify-content: flex-start; align-items: center;"
        >
            {#if $debug}
                #{job.id};
            {/if}
            <DisplayAddress job={jobClass} {simpleView} />
        </div>

        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <div
            class="date"
            class:switchable
            class:extended-background={expandedIndex === index}
            on:click={toggle}
        >
            <!--{job.process_stage}{editable(job)}-->
            <ListElementFriendlyDate
                disabled={!editable(job)}
                {friendlyTargetDate}
                {simpleView}
                isAdHoc={job.ad_hoc}
                jobId={job.id}
            />
        </div>

        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <div
            class="status"
            class:switchable
            class:extended-background={expandedIndex === index}
            on:click={toggle}
        >
            <JobStatusSegment
                jobType={job.special_attributes?.delivery_or_collection}
                jobStatus={getStatus(job)}
                isNewlyAdded={job.newly_added}
                isOptional={null}
                isOverdue={false}
                jobId={job.id}
                jobTargetDate={job.target_date ?? ''}
                bind:adHoc={job.ad_hoc}
                {simpleView}
                showAdhocIfExists={$debug}
            />
            {#if job.realisation?.service_attributes || $debug}
                <div style="padding-left: 5px; display: flex; align-items: center;">
                    <JobServiceAttributes
                        jobType={job.special_attributes?.delivery_or_collection}
                        serviceAttributes={job.realisation?.service_attributes}
                        scale={0.6}
                    />
                </div>
            {:else if (job.rejected && job.rejection?.service_attributes) || $debug}
                <div style="padding-left: 5px; display: flex; align-items: center;">
                    <JobServiceAttributes
                        jobType={job.special_attributes?.delivery_or_collection}
                        serviceAttributes={job.rejection?.service_attributes}
                        scale={0.6}
                    />
                </div>
            {/if}
        </div>

        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <div
            class="other-2 dont-break"
            style="justify-content: flex-start"
            class:switchable
            class:extended-background={expandedIndex === index}
            on:click={toggle}
        >
            <div style="display: flex; flex-direction: row; column-gap: 6px; align-items: center;">
                <!--{#if $debug}-->
                <!--    #{job.id}&nbsp;-->
                <!--    {#if job.completed === null}-->
                <!--        <LinkSmallBlack text="Set completed" on:click={setJobCompleted} />-->
                <!--    {:else}-->
                <!--        [Completed]-->
                <!--    {/if}-->
                <!--{/if}-->
                {#if job.coords_set_manually || $debug}
                    <span
                        use:tooltip={{
                            content: $_('Współrzędne ustawione ręcznie'),
                            position: 'left',
                        }}
                    >
                        <IconWarning scale={0.7} />
                    </span>
                {/if}
                {#if !job.coords_accepted || $debug}
                    <span use:tooltip={{ content: $_('low_quality_address'), position: 'left' }}>
                        <IconError scale={0.7} />
                    </span>
                {/if}
            </div>
        </div>
    {/if}

    {#if expandedIndex === index}
        <div class="expanded">
            {#if error}
                <ErrorInline text={$_(error)} />
            {/if}
            <FormSection>
                <FormRow>
                    <Label>{$_('target_date')}</Label>
                    <div style="width: 326px; display: flex; justify-content: flex-start">
                        <div style="flex: 0">
                            <SelectDay
                                disabled={!editable(job)}
                                minDay={new Date().toISOString().substring(0, 10)}
                                showRelativeToday={$clientSettings.display.showRelativeToday}
                                day={target_date}
                                onChange={d => {
                                    console.log('Selecting target_date for job', d);
                                    job.target_date = d;
                                    target_date = d;
                                    onUpdate();
                                }}
                            />
                        </div>
                    </div>
                </FormRow>
                {#if job.special_attributes}
                    <FormRow>
                        <Label>{$_('job_time_window')}</Label>
                        <div
                            style="width: 326px; display:flex; flex-direction: row; align-items: center; column-gap: 5px;"
                        >
                            <SelectHour
                                disabled={!editable(job)}
                                withBorder={false}
                                canBeEmpty={true}
                                hour={job.special_attributes.start_of_time_window || ''}
                                onChange={h => {
                                    if (job.special_attributes) {
                                        console.log('Selecting start_of_time_window for job', h);
                                        job.special_attributes.start_of_time_window = h || null;
                                        onUpdate();
                                        return true;
                                    }
                                    return false;
                                }}
                            />
                            -
                            <SelectHour
                                disabled={!editable(job)}
                                withBorder={false}
                                canBeEmpty={true}
                                hour={job.special_attributes.end_of_time_window || ''}
                                onChange={h => {
                                    if (job.special_attributes) {
                                        console.log('Selecting end_of_time_window for job', h);
                                        job.special_attributes.end_of_time_window = h || null;
                                        onUpdate();
                                        return true;
                                    }
                                    return false;
                                }}
                            />
                            {#if $app.account?.getSettingsReadonly()?.enable_job_modify_time_window_rule}
                                <JobModifyTimeWindowRuleMiniWidget
                                    {job}
                                    onRuleSaved={() => {
                                        console.log('Time window rule saved for job', job.id);
                                    }}
                                />
                            {/if}
                        </div>
                    </FormRow>
                {/if}
                <FormRow>
                    <Label>{$_('assigned_vehicle')}{get(debug) ? ' ' + job.vehicle_id : ''}</Label>
                    <div
                        style="width: 326px; display: flex; justify-content: flex-start; margin: 5px 0;"
                    >
                        <div style="flex: 0" data-assigned-vehicle-id={job.assigned_vehicle_id}>
                            {#if job.assigned_vehicle_id !== null}
                                <SelectVehicle
                                    styleMode="standard"
                                    selected={job.assigned_vehicle_id}
                                    disabled={true}
                                    onChange={() => {
                                        console.log('not implemented, disabled');
                                        return true;
                                    }}
                                />
                            {:else}
                                <SelectVehicle
                                    styleMode="standard"
                                    selected={job.vehicle_id}
                                    disabled={false}
                                    onChange={vehicleId => {
                                        console.log('vehicle_id', job.vehicle_id);
                                        job.vehicle_id = vehicleId;
                                        job = job;
                                        onUpdate();
                                        return true;
                                    }}
                                />
                            {/if}
                        </div>
                    </div>
                </FormRow>
            </FormSection>

            <!--            <div>-->
            <div style="display: flex; justify-content: center; font-size: 14px; padding: 2px;">
                {#if geocodingInProgress !== null}
                    {#if geocodingInProgress}
                        <Spinner />
                    {:else if job.geom && job.geom.coordinates}
                        <SuccessInline text={$_('Geocode success')} />
                    {:else}
                        <ErrorInline text={$_('Geocode failed')} />
                    {/if}
                {/if}
            </div>

            {#if job.address_attributes !== null}
                <div
                    style="display: flex; justify-content: center; font-size: 14px; padding: 4px 0;"
                >
                    <FormSection>
                        {#if job.special_attributes !== null}
                            <FormRow>
                                <Label>{$_('Nazwa zlecenia')}</Label>
                                <InputText
                                    disabled={!editable(job)}
                                    style="width: 310px;"
                                    value={jobClass.getLabel() || ''}
                                    on:change={e => {
                                        let newValue = e.target?.value || '';
                                        if (job.special_attributes === null)
                                            job.special_attributes = {};
                                        job.special_attributes = {
                                            ...job.special_attributes,
                                            label: newValue,
                                        };
                                        onUpdate();
                                    }}
                                />
                            </FormRow>
                        {/if}
                        <FormRow>
                            <Label>{$_('City')}</Label>
                            <InputText
                                disabled={!editable(job)}
                                style="width: 310px;"
                                value={(job?.address_attributes &&
                                    job?.address_attributes['city']) ||
                                    ''}
                                on:change={e => {
                                    let newValue = e.target?.value || '';
                                    if (job.address_attributes === null)
                                        job.address_attributes = {};
                                    job.address_attributes = {
                                        ...job.address_attributes,
                                        city: newValue,
                                    };
                                    onUpdate();
                                }}
                            />
                        </FormRow>
                        <FormRow>
                            <Label>{$_('Postal code')}</Label>
                            <InputText
                                disabled={!editable(job)}
                                style="width: 310px;"
                                value={(job?.address_attributes &&
                                    job?.address_attributes['pcode']) ||
                                    ''}
                                on:change={e => {
                                    let newValue = e.target?.value || '';
                                    if (job.address_attributes === null)
                                        job.address_attributes = {};
                                    job.address_attributes = {
                                        ...job.address_attributes,
                                        pcode: newValue,
                                    };
                                    onUpdate();
                                }}
                            />
                        </FormRow>
                        <FormRow>
                            <Label>{$_('Street')}</Label>
                            <InputText
                                disabled={!editable(job)}
                                style="width: 310px;"
                                value={(job?.address_attributes &&
                                    job?.address_attributes['street']) ||
                                    ''}
                                on:change={e => {
                                    let newValue = e.target?.value || '';
                                    if (job.address_attributes === null)
                                        job.address_attributes = {};
                                    job.address_attributes = {
                                        ...job.address_attributes,
                                        street: newValue,
                                    };
                                    onUpdate();
                                }}
                            />
                        </FormRow>
                        <FormRow>
                            <Label>{$_('House')}</Label>
                            <InputText
                                disabled={!editable(job)}
                                style="width: 310px;"
                                value={(job?.address_attributes &&
                                    job?.address_attributes['house']) ||
                                    ''}
                                on:change={e => {
                                    let newValue = e.target?.value || '';
                                    if (job.address_attributes === null)
                                        job.address_attributes = {};
                                    job.address_attributes = {
                                        ...job.address_attributes,
                                        house: newValue,
                                    };
                                    onUpdate();
                                }}
                            />
                        </FormRow>
                    </FormSection>
                </div>
            {/if}

            <TemplateExpandedSectionButtons>
                <div
                    style="display: flex; justify-content: space-between; align-items: center; width: 100%; flex-wrap: wrap; padding: 10px 15px; column-gap: 20px; row-gap: 10px;"
                >
                    <div>
                        {#if $features.feature_1475_job_history}
                            <Switch
                                bind:checked={$jobs.showHistory}
                                label={$_('show_job_history')}
                            />
                        {/if}
                    </div>
                    <div
                        style="display: flex; flex-direction:row;justify-content: space-between; align-items: center;  flex-wrap: wrap;"
                    >
                        {#if geocodingInProgress}
                            <!--                            <Spinner />-->
                        {:else if job.geom && job.geom.coordinates}
                            <!--{#if enableMap}-->
                            <!--    <IconLocationOn-->
                            <!--        on:click={() => {-->
                            <!--            console.log('job.geom', job.geom);-->
                            <!--            if (job.geom && job.geom.coordinates) {-->
                            <!--                Targeo.Base.Map.zoomAndCenter(-->
                            <!--                    {-->
                            <!--                        x: job.geom.coordinates[0],-->
                            <!--                        y: job.geom.coordinates[1],-->
                            <!--                    },-->
                            <!--                    21-->
                            <!--                );-->
                            <!--                jobsOnMap.jobShowBalloon(job.id);-->
                            <!--            }-->
                            <!--        }}-->
                            <!--    />-->
                            <!--{/if}-->
                        {:else}
                            <div style="opacity:0.5; display: flex;">
                                <ErrorInline text={$_('No coordinates')} />
                                <ErrorColor>
                                    <IconLocationOn />
                                </ErrorColor>
                            </div>
                        {/if}
                        <ButtonSmallBlack
                            text={$_('Geocode again')}
                            disabled={geocodingInProgress === true || !editable(job)}
                            showIcon={false}
                            on:click={() => {
                                (async () => {
                                    geocodingInProgress = true;
                                    job.geom = null;
                                    try {
                                        await onUpdate();
                                        let response = await JobApi.geocode(get(api), job.id);
                                        console.log('geocoded', response.coords_accepted);
                                        job = { ...response };
                                        dispatch('setcoordinates');
                                        geocodingInProgress = false;
                                    } catch (e) {
                                        console.error('geocode error', e);
                                        alert(e);
                                    }
                                })();
                            }}
                        />
                        {#if switchable && enableMap}
                            <ButtonSmallBlack
                                text={$_('Set on the map')}
                                disabled={geocodingInProgress === true || !editable(job)}
                                showIcon={false}
                                on:click={() => {
                                    console.log('job.geom', job?.geom);
                                    if (job?.geom && job?.geom?.coordinates) {
                                        Targeo.Base.Map.zoomAndCenter(
                                            {
                                                x: job.geom.coordinates[0],
                                                y: job.geom.coordinates[1],
                                            },
                                            21
                                        );
                                        jobsOnMap.jobShowBalloon(job.id);
                                    }
                                    startPointOnMap();
                                }}
                            />
                        {/if}
                        <ButtonSmallBlack
                            text={$_('Edit')}
                            disabled={!editable(job)}
                            showIcon={false}
                            on:click={() => {
                                Navigation.navigate('/edit-job/'+job.id);
                            }}
                        />
                    </div>
                </div>
            </TemplateExpandedSectionButtons>
            <!--            </div>-->

            {#if job.client_attributes}
                <TemplateExpandedSection>
                    <JobOneAttributes jobId={job.id} {job} expanded={false} />
                </TemplateExpandedSection>
            {/if}

            {#if $jobs.showHistory && $features.feature_1475_job_history}
                <TemplateExpandedSection>
                    <JobHistory jobId={job.id} {job} />
                </TemplateExpandedSection>
            {/if}

            {#if !switchable}
                <div style="padding:20px;">
                    {#if error}
                        <MessageDanger {error} />
                    {/if}
                    <TemplateExpandedSectionButtons>
                        <ButtonSmallBlack
                            text={$_('Save')}
                            on:click={() => {
                                onUpdate;
                                if (switchable) {
                                    expandedIndex = -1;
                                } else if (closeButton) {
                                    dispatch('close');
                                }
                            }}
                        />
                        {#if closeButton}
                            <ButtonSmallBlack
                                text={$_('Close')}
                                on:click={() => {
                                    dispatch('close');
                                }}
                            />
                        {/if}
                    </TemplateExpandedSectionButtons>
                </div>
            {/if}
        </div>
    {/if}
</div>

<style>
    .city {
        color: #000;
    }
    .street {
        color: #888;
    }

    :global(.job-list-overlay) .checkbox {
        display: none;
    }
    :global(.job-list-overlay) .order-element-data {
        border-bottom: unset;
    }
</style>
